/**
 * The cross-section — the signature element (docs/UI.md).
 *
 * It is the reason this is a simulator and not a spreadsheet, and it earns the whole hero area.
 *
 * Rules it obeys:
 *  - **True vertical scale for water elevations.** One linear map from metres ASL to screen y is
 *    shared by the reservoir surface, every datum line, the tailrace and the downstream gauge. The
 *    gap between the surface and the crest is the freeboard, and it is the same distance on screen
 *    that it is in metres. Horizontal is schematic; vertical is not.
 *  - **Gates draw at their actual opening**, and each nappe's width goes as √Q so the spill
 *    *looks* like its discharge.
 *  - Runner rotation follows unit speed; a unit at SNL visibly turns slower than one at 150 rpm.
 *  - Colour is never the only carrier of state — every abnormal element also gets a label.
 */

import { useMemo } from 'react';
import type { Snapshot } from '../../engine/snapshot';
import type { SiteConfig } from '../../engine/types';

interface Props {
  site: SiteConfig;
  snap: Snapshot;
  reducedMotion: boolean;
  onSelectGate(i: number): void;
  onSelectUnit(i: number): void;
  selectedGate: number;
  selectedUnit: number;
}

const W = 1200;
const H = 560;
const TOP = 54; // sky band below this
const BOTTOM = 505;

export function CrossSection({
  site,
  snap,
  reducedMotion,
  onSelectGate,
  onSelectUnit,
  selectedGate,
  selectedUnit,
}: Props): JSX.Element {
  const dam = site.dam;

  // One elevation → y map for everything that holds water.
  const { eMin, eMax, y } = useMemo(() => {
    const lo = Math.min(site.tailwater.kind === 'power' ? site.tailwater.base : 96, dam.mol) - 14;
    const hi = dam.parapet + 8;
    const map = (e: number): number => BOTTOM - ((e - lo) / (hi - lo)) * (BOTTOM - TOP);
    return { eMin: lo, eMax: hi, y: map };
  }, [site, dam]);

  const hw = snap.headwater;
  const tw = snap.tailwater;
  const yHw = y(hw);
  const yTw = y(tw);

  // Schematic horizontal layout.
  const damLeft = 430;
  const damRight = 560;
  const chuteEnd = 690;
  const tailraceEnd = 900;

  const gateCount = Math.max(1, site.spillway.gateCount);
  const gateBandLeft = damLeft + 6;
  const gateBandWidth = damRight - damLeft - 12;
  const gateW = gateBandWidth / gateCount;

  const totalSpill = snap.spillFlow;
  const anyAlarm = snap.alarms.some((a) => a.priority === 'emergency' || a.priority === 'high');

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Dam cross-section. Headwater ${hw.toFixed(2)} metres, tailwater ${tw.toFixed(2)} metres, total release ${Math.round(snap.totalOutflow)} cubic metres per second.`}
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id="water" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--water-shallow)" />
          <stop offset="45%" stopColor="var(--water)" />
          <stop offset="100%" stopColor="var(--water-deep)" />
        </linearGradient>
        <linearGradient id="tailwater" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--water-moving)" />
          <stop offset="100%" stopColor="var(--water)" />
        </linearGradient>
        <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#12171b" />
          <stop offset="100%" stopColor="var(--surface-0)" />
        </linearGradient>
        <clipPath id="reservoirClip">
          <rect x={0} y={TOP} width={damLeft} height={BOTTOM - TOP} />
        </clipPath>
        <clipPath id="tailraceClip">
          <rect x={chuteEnd - 40} y={TOP} width={tailraceEnd - chuteEnd + 40} height={BOTTOM - TOP} />
        </clipPath>
      </defs>

      {/* Sky and weather band */}
      <rect x={0} y={0} width={W} height={TOP} fill="url(#sky)" />
      <WeatherBand snap={snap} y={y} eMin={eMin} eMax={eMax} />

      {/* ── Datum lines, drawn first so water covers their submerged part ────────── */}
      <Datums
        width={damRight + 40}
        items={[
          { label: 'Parapet', value: dam.parapet, tone: 'alarm' },
          { label: 'Crest', value: dam.crest, tone: 'dim' },
          { label: 'MFL', value: dam.mfl, tone: 'warn' },
          { label: 'FSL', value: dam.fsl, tone: 'dim' },
          { label: 'MOL', value: dam.mol, tone: 'dim' },
        ]}
        y={y}
      />

      {/* Rule-curve band as a filled tint, with the target as a dashed line. */}
      <rect
        x={0}
        y={y(snap.ruleCurveUpper)}
        width={damLeft}
        height={Math.max(1, y(snap.ruleCurveLower) - y(snap.ruleCurveUpper))}
        fill="var(--ok)"
        opacity={0.1}
      />
      <line
        x1={0}
        x2={damLeft}
        y1={y(snap.ruleCurveTarget)}
        y2={y(snap.ruleCurveTarget)}
        stroke="var(--ok)"
        strokeWidth={1}
        strokeDasharray="6 4"
        opacity={0.8}
      />
      <text x={8} y={y(snap.ruleCurveTarget) - 5} fill="var(--ok)" fontSize={10} className="num">
        rule curve {snap.ruleCurveTarget.toFixed(1)} m
      </text>

      {/* ── Reservoir ─────────────────────────────────────────────────────────────── */}
      <g clipPath="url(#reservoirClip)">
        <WaterBody
          top={yHw}
          bottom={BOTTOM}
          left={-140}
          right={damLeft}
          fill="url(#water)"
          reducedMotion={reducedMotion}
        />
      </g>
      {/* Reservoir bed */}
      <path
        d={`M 0 ${BOTTOM} L 0 ${y(dam.mol) + 26} Q 150 ${BOTTOM - 6} ${damLeft} ${BOTTOM} Z`}
        fill="var(--surface-2)"
        opacity={0.55}
      />

      {/* ── Freeboard callout ─────────────────────────────────────────────────────── */}
      <FreeboardCallout x={330} yTop={y(dam.crest)} yBottom={yHw} metres={dam.crest - hw} />

      {/* ── Dam body ──────────────────────────────────────────────────────────────── */}
      <path
        d={`M ${damLeft - 46} ${BOTTOM} L ${damLeft + 4} ${y(dam.crest)} L ${damRight - 4} ${y(dam.crest)} L ${damRight + 52} ${BOTTOM} Z`}
        fill="var(--surface-2)"
        stroke="var(--line)"
      />
      {/* Parapet wall */}
      <rect
        x={damLeft + 2}
        y={y(dam.parapet)}
        width={damRight - damLeft - 4}
        height={Math.max(2, y(dam.crest) - y(dam.parapet))}
        fill="var(--surface-2)"
        stroke="var(--line)"
      />

      {/* ── Spillway gates, drawn at their actual opening ─────────────────────────── */}
      {site.spillway.kind === 'gated' &&
        snap.gates.map((g, i) => (
          <Gate
            key={g.id}
            index={i}
            gate={g}
            site={site}
            x={gateBandLeft + i * gateW}
            width={gateW - 2}
            y={y}
            selected={selectedGate === i}
            onSelect={() => onSelectGate(i)}
          />
        ))}

      {/* Spillway chute and nappes */}
      <path
        d={`M ${damRight - 4} ${y(site.spillway.sill)} Q ${chuteEnd - 60} ${y(site.spillway.sill) + 40} ${chuteEnd} ${yTw}`}
        fill="none"
        stroke="var(--line)"
        strokeWidth={2}
      />
      {snap.gates.map((g, i) =>
        g.q > 1 ? (
          <Nappe
            key={`n${g.id}`}
            x={gateBandLeft + i * gateW + gateW / 2}
            yStart={y(site.spillway.sill)}
            xEnd={chuteEnd}
            yEnd={yTw}
            q={g.q}
            reducedMotion={reducedMotion}
          />
        ) : null,
      )}

      {/* ── Low-level outlets ─────────────────────────────────────────────────────── */}
      {snap.llos.map((l, i) => (
        <g key={`llo${l.id}`}>
          <rect
            x={damLeft + 10 + i * 22}
            y={y(site.llo.centreline) - 4}
            width={16}
            height={8}
            fill={l.openFrac > 0.01 ? 'var(--water-moving)' : 'var(--surface-1)'}
            stroke="var(--line)"
          />
          {l.q > 1 && (
            <path
              d={`M ${damLeft + 26 + i * 22} ${y(site.llo.centreline)} Q ${damRight + 40} ${y(site.llo.centreline) + 20} ${chuteEnd - 20} ${yTw}`}
              stroke="var(--water-moving)"
              strokeWidth={Math.max(1.5, Math.sqrt(l.q) / 3)}
              fill="none"
              opacity={0.85}
            />
          )}
        </g>
      ))}
      {snap.lloFlow > 1 && (
        <text x={damLeft + 8} y={y(site.llo.centreline) - 10} fontSize={9} fill="var(--water-moving)" className="num">
          LLO {Math.round(snap.lloFlow)} m³/s · TDG {site.llo.tdgPercent}%
        </text>
      )}

      {/* ── Powerhouse ────────────────────────────────────────────────────────────── */}
      <Powerhouse
        site={site}
        snap={snap}
        x={damRight + 56}
        yBase={BOTTOM}
        yIntake={y(dam.mol) + 18}
        selectedUnit={selectedUnit}
        onSelectUnit={onSelectUnit}
        reducedMotion={reducedMotion}
      />

      {/* ── Tailrace ──────────────────────────────────────────────────────────────── */}
      <g clipPath="url(#tailraceClip)">
        <WaterBody
          top={yTw}
          bottom={BOTTOM}
          left={chuteEnd - 40}
          right={tailraceEnd + 40}
          fill="url(#tailwater)"
          reducedMotion={reducedMotion}
          moving
        />
      </g>
      <line x1={chuteEnd - 40} x2={tailraceEnd} y1={BOTTOM} y2={BOTTOM} stroke="var(--line)" />
      <text x={chuteEnd + 6} y={yTw - 7} fontSize={10} fill="var(--ink-dim)" className="num">
        tailwater {tw.toFixed(2)} m
      </text>

      {/* ── Downstream strip to the gauge ─────────────────────────────────────────── */}
      <DownstreamStrip
        site={site}
        snap={snap}
        x0={tailraceEnd}
        x1={W - 8}
        yBase={BOTTOM}
        yWater={yTw}
        reducedMotion={reducedMotion}
      />

      {/* Headwater readout, pinned to the surface */}
      <g transform={`translate(14 ${Math.max(TOP + 14, yHw - 10)})`}>
        <text fontSize={13} fill="var(--ink)" className="num" fontWeight={600}>
          {hw.toFixed(2)} m
        </text>
        <text y={13} fontSize={10} fill="var(--ink-dim)">
          headwater
        </text>
      </g>

      {totalSpill > 1 && (
        <text x={damRight + 8} y={TOP + 16} fontSize={11} fill="var(--water-moving)" className="num">
          spilling {Math.round(totalSpill).toLocaleString('en-US')} m³/s
        </text>
      )}
      {anyAlarm && (
        <text x={W - 10} y={TOP + 16} fontSize={11} fill="var(--alarm)" textAnchor="end" fontWeight={600}>
          ▲ {snap.alarms.filter((a) => a.priority === 'emergency' || a.priority === 'high').length} active
        </text>
      )}
    </svg>
  );
}

/* ────────────────────────────── pieces ────────────────────────────── */

interface DatumSpec {
  label: string;
  value: number;
  tone: 'dim' | 'warn' | 'alarm';
}

const TONE_STROKE: Record<DatumSpec['tone'], string> = {
  alarm: 'var(--alarm)',
  warn: 'var(--warn)',
  dim: 'var(--ink-dim)',
};

/**
 * Datum lines with collision-avoided labels.
 *
 * The *lines* sit at true elevation — that is the whole point of the drawing. The *labels* cannot:
 * at Kettle Narrows the parapet and crest are 1.5 m apart, which at true scale is about three
 * pixels, so their text overlaps into an unreadable smear. Labels are pushed apart to a minimum
 * spacing and joined to their line with a short leader, so the line stays honest and the text
 * stays legible.
 */
function Datums({
  items,
  y,
  width,
}: {
  items: DatumSpec[];
  y: (e: number) => number;
  width: number;
}): JSX.Element {
  const MIN_GAP = 12;
  // Sort top-down, then push each label down until it clears the one above it.
  const sorted = [...items].sort((a, b) => b.value - a.value);
  const placed: { spec: DatumSpec; lineY: number; labelY: number }[] = [];
  let lastLabelY = -Infinity;
  for (const spec of sorted) {
    const lineY = y(spec.value);
    const labelY = Math.max(lineY, lastLabelY + MIN_GAP);
    lastLabelY = labelY;
    placed.push({ spec, lineY, labelY });
  }

  return (
    <g>
      {placed.map(({ spec, lineY, labelY }) => {
        const stroke = TONE_STROKE[spec.tone];
        return (
          <g key={spec.label} opacity={spec.tone === 'dim' ? 0.55 : 0.85}>
            <line x1={0} x2={width} y1={lineY} y2={lineY} stroke={stroke} strokeWidth={1} strokeDasharray="2 3" />
            {/* Leader from the true line to the displaced label. */}
            {Math.abs(labelY - lineY) > 1 && (
              <path
                d={`M ${width} ${lineY} L ${width + 5} ${labelY - 3}`}
                stroke={stroke}
                strokeWidth={0.75}
                fill="none"
              />
            )}
            <text x={width + 7} y={labelY + 3} fontSize={10} fill={stroke} className="num">
              {spec.label} {spec.value.toFixed(1)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/**
 * A body of water. The surface animates with a slow two-frequency displacement — slow enough to
 * read as water, not as a loading spinner. Reduced motion renders it flat.
 */
function WaterBody({
  top,
  bottom,
  left,
  right,
  fill,
  reducedMotion,
  moving = false,
}: {
  top: number;
  bottom: number;
  left: number;
  right: number;
  fill: string;
  reducedMotion: boolean;
  moving?: boolean;
}): JSX.Element {
  const amp = moving ? 2.4 : 1.6;
  const span = right - left + 240;
  const pts: string[] = [];
  for (let x = 0; x <= span; x += 12) {
    const yy = top + Math.sin(x / 46) * amp + Math.sin(x / 17) * amp * 0.45;
    pts.push(`${left + x} ${yy.toFixed(2)}`);
  }
  const d = `M ${left - 240} ${bottom} L ${left - 240} ${top} L ${pts.join(' L ')} L ${right + 240} ${bottom} Z`;
  return (
    <path
      d={d}
      fill={fill}
      className={reducedMotion ? undefined : 'wave'}
      style={reducedMotion ? undefined : { animationDuration: moving ? '5.5s' : '9s' }}
    />
  );
}

function FreeboardCallout({
  x,
  yTop,
  yBottom,
  metres,
}: {
  x: number;
  yTop: number;
  yBottom: number;
  metres: number;
}): JSX.Element {
  const low = metres < 2;
  const colour = low ? 'var(--alarm)' : metres < 5 ? 'var(--warn)' : 'var(--ink-dim)';
  return (
    <g>
      <line x1={x} x2={x} y1={yTop} y2={yBottom} stroke={colour} strokeWidth={1} />
      <line x1={x - 4} x2={x + 4} y1={yTop} y2={yTop} stroke={colour} strokeWidth={1} />
      <line x1={x - 4} x2={x + 4} y1={yBottom} y2={yBottom} stroke={colour} strokeWidth={1} />
      <text
        x={x + 7}
        y={(yTop + yBottom) / 2 + 3}
        fontSize={11}
        fill={colour}
        className="num"
        fontWeight={low ? 700 : 500}
      >
        freeboard {metres.toFixed(2)} m{low ? ' ▲' : ''}
      </text>
    </g>
  );
}

function Gate({
  index,
  gate,
  site,
  x,
  width,
  y,
  selected,
  onSelect,
}: {
  index: number;
  gate: Snapshot['gates'][number];
  site: SiteConfig;
  x: number;
  width: number;
  y: (e: number) => number;
  selected: boolean;
  onSelect(): void;
}): JSX.Element {
  const sill = site.spillway.sill;
  const leafBottom = sill + gate.opening;
  const leafTop = leafBottom + site.spillway.gateHeight;
  const ySill = y(sill);
  const yLeafBottom = y(leafBottom);
  const yLeafTop = y(leafTop);
  const faulted = gate.hoist !== 'ok';
  const stroke = faulted ? 'var(--alarm)' : selected ? 'var(--ink)' : 'var(--line)';

  return (
    <g onClick={onSelect} style={{ cursor: 'pointer' }} role="button" aria-label={`Gate ${index + 1}`}>
      {/* Opening beneath the leaf */}
      <rect x={x} y={yLeafBottom} width={width} height={Math.max(0, ySill - yLeafBottom)} fill="var(--water-deep)" />
      {/* The leaf itself, at its real elevation */}
      <rect
        x={x}
        y={yLeafTop}
        width={width}
        height={Math.max(3, yLeafBottom - yLeafTop)}
        fill="var(--surface-1)"
        stroke={stroke}
        strokeWidth={selected ? 2 : 1}
      />
      <text x={x + width / 2} y={yLeafTop - 4} fontSize={9} fill="var(--ink-dim)" textAnchor="middle" className="num">
        {index + 1}
      </text>
      {gate.opening > 0.005 && (
        <text x={x + width / 2} y={ySill + 12} fontSize={9} fill="var(--water-moving)" textAnchor="middle" className="num">
          {gate.opening.toFixed(1)}
        </text>
      )}
      {faulted && (
        <text x={x + width / 2} y={yLeafTop - 14} fontSize={9} fill="var(--alarm)" textAnchor="middle" fontWeight={700}>
          ✕
        </text>
      )}
      {gate.heaterOn && (
        <circle cx={x + width - 4} cy={yLeafTop + 6} r={2.5} fill="var(--warn)">
          <title>Gate heater on ({Math.round(gate.heaterMinutes)} min)</title>
        </circle>
      )}
    </g>
  );
}

/** Nappe width goes as √Q so the spill *looks* like its discharge (UI.md). */
function Nappe({
  x,
  yStart,
  xEnd,
  yEnd,
  q,
  reducedMotion,
}: {
  x: number;
  yStart: number;
  xEnd: number;
  yEnd: number;
  q: number;
  reducedMotion: boolean;
}): JSX.Element {
  const w = Math.max(1.5, Math.sqrt(q) / 2.6);
  return (
    <path
      d={`M ${x} ${yStart} C ${x + 24} ${yStart + 24}, ${xEnd - 70} ${yEnd - 34}, ${xEnd - 4} ${yEnd}`}
      stroke="var(--water-moving)"
      strokeWidth={w}
      strokeLinecap="round"
      fill="none"
      opacity={0.9}
      className={reducedMotion ? undefined : 'nappe'}
    />
  );
}

function Powerhouse({
  site,
  snap,
  x,
  yBase,
  yIntake,
  selectedUnit,
  onSelectUnit,
  reducedMotion,
}: {
  site: SiteConfig;
  snap: Snapshot;
  x: number;
  yBase: number;
  yIntake: number;
  selectedUnit: number;
  onSelectUnit(i: number): void;
  reducedMotion: boolean;
}): JSX.Element {
  const n = snap.units.length;
  const boxW = Math.min(26, 120 / n);
  const totalW = n * (boxW + 4);
  const top = yBase - 62;
  return (
    <g>
      <rect x={x - 6} y={top - 10} width={totalW + 12} height={72} fill="var(--surface-1)" stroke="var(--line)" />
      <text x={x - 6} y={top - 15} fontSize={9} fill="var(--ink-dim)">
        POWERHOUSE
      </text>
      {snap.units.map((u, i) => {
        const ux = x + i * (boxW + 4);
        const online = u.mode === 'online';
        const tripped = u.mode === 'tripped';
        const colour = tripped ? 'var(--alarm)' : online ? 'var(--water-moving)' : 'var(--ink-dim)';
        // Rotation period from actual speed: a unit at SNL visibly turns slower than one at rated.
        const rpm = Math.max(0, u.rpm);
        const period = rpm > 1 ? Math.max(0.25, 60 / rpm) : 0;
        return (
          <g key={u.id} onClick={() => onSelectUnit(i)} style={{ cursor: 'pointer' }}>
            {/* Penstock from the intake */}
            <path
              d={`M ${ux + boxW / 2} ${yIntake} L ${ux + boxW / 2} ${top + 8}`}
              stroke={u.q > 1 ? 'var(--water)' : 'var(--line)'}
              strokeWidth={u.q > 1 ? Math.max(1.5, Math.sqrt(u.q) / 4) : 1}
              fill="none"
            />
            <rect
              x={ux}
              y={top + 8}
              width={boxW}
              height={44}
              fill="var(--surface-2)"
              stroke={selectedUnit === i ? 'var(--ink)' : 'var(--line)'}
              strokeWidth={selectedUnit === i ? 2 : 1}
            />
            <g transform={`translate(${ux + boxW / 2} ${top + 26})`}>
              <g
                className={period > 0 && !reducedMotion ? 'runner' : undefined}
                style={period > 0 && !reducedMotion ? { animationDuration: `${period}s` } : undefined}
              >
                <circle r={7} fill="none" stroke={colour} strokeWidth={1.4} />
                <line x1={-7} y1={0} x2={7} y2={0} stroke={colour} strokeWidth={1.4} />
                <line x1={0} y1={-7} x2={0} y2={7} stroke={colour} strokeWidth={1.4} />
              </g>
            </g>
            <text
              x={ux + boxW / 2}
              y={top + 47}
              fontSize={8}
              fill={colour}
              textAnchor="middle"
              className="num"
            >
              {online ? Math.round(u.mw) : tripped ? 'TRIP' : u.mode === 'starting' ? 'STRT' : '—'}
            </text>
            <text x={ux + boxW / 2} y={top + 5} fontSize={8} fill="var(--ink-dim)" textAnchor="middle" className="num">
              {i + 1}
            </text>
          </g>
        );
      })}
      <text x={x - 6} y={yBase + 14} fontSize={10} fill="var(--ink-dim)" className="num">
        {Math.round(snap.exportMW)} MW export · {Math.round(snap.turbineFlow)} m³/s
      </text>
      <title>{site.name} powerhouse</title>
    </g>
  );
}

/**
 * The downstream strip compresses the reach horizontally but keeps the gauge stage on the same
 * vertical scale as the tailrace (UI.md).
 */
function DownstreamStrip({
  site,
  snap,
  x0,
  x1,
  yBase,
  yWater,
  reducedMotion,
}: {
  site: SiteConfig;
  snap: Snapshot;
  x0: number;
  x1: number;
  yBase: number;
  yWater: number;
  reducedMotion: boolean;
}): JSX.Element {
  const ds = site.downstream;
  const g = snap.gaugeFlow;
  const stage = g >= ds.floodMajor ? 'major' : g >= ds.floodModerate ? 'moderate' : g >= ds.floodMinor ? 'minor' : null;
  const colour = stage === 'major' ? 'var(--alarm)' : stage === 'moderate' ? 'var(--alarm)' : stage === 'minor' ? 'var(--warn)' : 'var(--water)';
  // Stage rise at the gauge, scaled off the flood thresholds, on the same vertical scale.
  const rise = Math.min(30, (g / ds.floodMajor) * 22);
  const yGauge = yBase - 8 - rise;

  return (
    <g>
      <line x1={x0} x2={x0} y1={yWater - 30} y2={yBase} stroke="var(--line)" strokeDasharray="2 3" />
      <path
        d={`M ${x0} ${yBase} L ${x1} ${yBase} L ${x1} ${yGauge} Q ${(x0 + x1) / 2} ${yGauge - 4} ${x0} ${yWater} Z`}
        fill={colour}
        opacity={stage ? 0.55 : 0.4}
        className={reducedMotion ? undefined : 'wave'}
        style={reducedMotion ? undefined : { animationDuration: '7s' }}
      />
      <text x={x0 + 6} y={yWater - 32} fontSize={9} fill="var(--ink-dim)">
        ← {ds.distanceKm} km →
      </text>
      <text x={x1} y={yGauge - 8} fontSize={10} fill={colour} textAnchor="end" className="num" fontWeight={stage ? 700 : 400}>
        {ds.gaugeName} {Math.round(g).toLocaleString('en-US')} m³/s
      </text>
      {stage && (
        <text x={x1} y={yGauge - 21} fontSize={10} fill={colour} textAnchor="end" fontWeight={700}>
          ▲ {stage.toUpperCase()} FLOOD STAGE
        </text>
      )}
      {snap.gaugeStale && (
        <text x={x1} y={yBase + 13} fontSize={9} fill="var(--warn)" textAnchor="end">
          ⚠ gauge data stale
        </text>
      )}
    </g>
  );
}

function WeatherBand({
  snap,
  y,
  eMin,
  eMax,
}: {
  snap: Snapshot;
  y: (e: number) => number;
  eMin: number;
  eMax: number;
}): JSX.Element {
  const raining = snap.precipMmH > 0.05;
  const drops = raining ? Math.min(28, Math.round(snap.precipMmH * 14) + 5) : 0;
  const snowVisible = snap.snowline < eMax && snap.snowline > eMin;
  return (
    <g>
      <text x={12} y={20} fontSize={11} fill="var(--ink-dim)" className="num">
        {snap.tempC.toFixed(1)} °C
      </text>
      <text x={86} y={20} fontSize={11} fill="var(--ink-dim)" className="num">
        {snap.precipMmH.toFixed(2)} mm/h
      </text>
      <text x={188} y={20} fontSize={11} fill="var(--ink-dim)" className="num">
        snowline {Math.round(snap.snowline)} m
      </text>
      <text x={320} y={20} fontSize={11} fill="var(--ink-dim)" className="num">
        inflow {Math.round(snap.inflowGauged).toLocaleString('en-US')} m³/s
      </text>
      <text x={470} y={20} fontSize={11} fill="var(--ink-dim)" className="num">
        release {Math.round(snap.totalOutflow).toLocaleString('en-US')} m³/s
      </text>
      {Array.from({ length: drops }).map((_, i) => {
        const x = 20 + ((i * 137) % (W - 60));
        return <line key={i} x1={x} y1={26 + (i % 3) * 7} x2={x - 3} y2={34 + (i % 3) * 7} stroke="var(--water-shallow)" strokeWidth={1} opacity={0.5} />;
      })}
      {snowVisible && (
        <line
          x1={0}
          x2={200}
          y1={y(snap.snowline)}
          y2={y(snap.snowline)}
          stroke="var(--ink-dim)"
          strokeDasharray="1 5"
          opacity={0.5}
        />
      )}
    </g>
  );
}
